import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import type { GateCloseFrontier } from '~/models/gateClose'
import {
  type EditStateMap,
  isKomaManual,
  makeEditRowKey,
} from '~/pages/Supply48Data/models/editState'
import { isCellLocked } from '~/pages/Supply48Data/models/gcLock'
import type { DayIndex } from '~/pages/Supply48Data/models/saveRows'

export type ManualEditCounts = {
  /** 表示中の全供給源を合計した手動変更コマ数 */
  total: number
  /** legId → その供給源の手動変更コマ数 (0 件の leg は entry を持たない) */
  byLeg: Map<string, number>
}

type CountArgs = {
  /**
   * 数える対象の source id 一覧。呼び出し側は編集可能 source
   * (`sourceKind.ts` の `isEditableSource` allow-list) だけに絞って渡すこと —
   * revert 実装 (`doRevertLegToAuto` / `doRevertAllToAuto`) も同じ allow-list
   * で非編集 source (市場等) を skip するため、絞らずに全 source id を渡すと
   * 「数えた件数 = 戻る件数」の不変条件が崩れる (= 戻らない件数まで数えてしまう)。
   */
  legIds: string[]
  visibleDates: ProtoDate[]
  dayIndex: DayIndex
  editState: EditStateMap
  gateClose: GateCloseFrontier | undefined
}

/**
 * AC-S4D-008 のリセット操作が対象にする手動変更コマ数を数える。
 *
 * 用途は 2 つ:
 *   - リセット操作の表示ゲート (WHILE 手動変更が 1 件以上ある場合)
 *   - 確認ダイアログの件数明示
 *
 * 「手動変更」の判定はリセット対象件数の共通規則 `isKomaManual` を使う。
 * セルの視覚区別 (AC-S4D-049) は「編集中」(`isKomaEditing`、保存前) と
 * 「変更後」(`isKomaSavedManual`、保存済み) の 2 レイヤに分かれているが、
 * `isKomaSavedManual` は `savedManualKomas` に絞った上で `isKomaManual` の
 * 結果をそのまま使う (editState.ts 参照) ため、 画面上で「編集中」または
 * 「変更後」いずれかの印が付いているコマ数の合計と、 ここで数える件数は一致する。
 *
 * 除外条件は revert 実装 (`useSupply48EditSession` の `doRevertLegToAuto` /
 * `doRevertAllToAuto`) が実際に戻せる範囲に合わせる (= 数えた件数がそのまま
 * 「戻る件数」になる):
 *   - GC ロック済みコマ・過去日 — revert が据え置く
 *   - dayIndex に行が無い受渡日 (新規行) — revert が continue でスキップする
 *   - autoSlots が全て未設定の行 — `revertRowToAuto` の hasAuto ガードで no-op
 *     (自動計算値スナップショットが無く、戻り先のベースラインが存在しない)
 */
export function countManualEdits({
  legIds,
  visibleDates,
  dayIndex,
  editState,
  gateClose,
}: CountArgs): ManualEditCounts {
  const byLeg = new Map<string, number>()
  let total = 0

  for (const legId of legIds) {
    let legCount = 0
    for (const date of visibleDates) {
      const key = makeEditRowKey(legId, date.year, date.month, date.day)
      const day = dayIndex.get(key)
      // dayIndex に行が無い受渡日は revert がスキップするため数えない
      if (day === undefined) continue
      // autoSlots が全て未設定の行は revertRowToAuto が no-op のため数えない
      // (value?: number — 0 は有効値、未設定コマだけが undefined)
      if (!day.autoSlots.some((s) => s.value !== undefined)) continue
      const editRow = editState.get(key)
      // 手動変更になり得るコマは保存済み / セッションのいずれかに載っているものだけ
      const candidates = new Set<number>(day.manualEditKomas)
      for (const koma of editRow?.manualEditKomas ?? []) {
        candidates.add(koma)
      }
      for (const koma of candidates) {
        if (isCellLocked(gateClose, day, date, koma)) continue
        if (!isKomaManual(editRow, day.manualEditKomas, koma)) continue
        legCount += 1
      }
    }
    if (legCount > 0) {
      byLeg.set(legId, legCount)
      total += legCount
    }
  }

  return { total, byLeg }
}
